import React from 'react';
import {
  Avatar,
  Box,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Rating,
  Tooltip,
  Typography,
} from '@mui/material';
import { RATING_CATEGORIES, type FeedbackEntry } from '../types';
import { averageRating } from '../helpers/ratingHelpers';
import { formatRelativeTime } from '../helpers/timeHelpers';

type FeedbackHistoryListProps = {
  entries: FeedbackEntry[];
};

export const FeedbackHistoryList: React.FC<FeedbackHistoryListProps> = ({ entries }) => (
  <List disablePadding>
    {entries.map((entry, idx) => {
      const avg = averageRating(entry.ratings);
      return (
        <React.Fragment key={entry.id}>
          {idx > 0 && <Divider component="li" />}
          <ListItem alignItems="flex-start" sx={{ px: 0, py: 1.5 }}>
            <ListItemAvatar sx={{ minWidth: 44 }}>
              <Avatar
                sx={{
                  width: 36,
                  height: 36,
                  fontSize: '0.75rem',
                  bgcolor: entry.avatarColor,
                }}
              >
                {entry.targetInitials}
              </Avatar>
            </ListItemAvatar>
            <ListItemText
              primaryTypographyProps={{ component: 'div' }}
              secondaryTypographyProps={{ component: 'div' }}
              primary={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Typography variant="subtitle2" component="span">
                    {entry.targetName}
                  </Typography>
                  <Tooltip title={`Avg: ${avg.toFixed(1)} / 5`} arrow>
                    <span>
                      <Rating
                        value={avg}
                        precision={0.5}
                        size="small"
                        readOnly
                        sx={{ fontSize: '0.9rem' }}
                      />
                    </span>
                  </Tooltip>
                  <Chip
                    label={formatRelativeTime(entry.submittedAt)}
                    size="small"
                    variant="outlined"
                    sx={{ height: 18, fontSize: '0.65rem' }}
                  />
                </Box>
              }
              secondary={
                <Box sx={{ mt: 0.5 }}>
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 0.5 }}>
                    {RATING_CATEGORIES.filter((cat) => (entry.ratings[cat.key] ?? 0) > 0).map(
                      (cat) => (
                        <Chip
                          key={cat.key}
                          label={`${cat.label}: ${entry.ratings[cat.key]}★`}
                          size="small"
                          sx={{ height: 20, fontSize: '0.68rem' }}
                          color="primary"
                          variant="outlined"
                        />
                      )
                    )}
                  </Box>
                  {entry.feedback && (
                    <Typography
                      component="span"
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        fontStyle: 'italic',
                        fontSize: '0.8rem',
                        mt: 0.25,
                        display: 'block',
                      }}
                    >
                      "{entry.feedback}"
                    </Typography>
                  )}
                </Box>
              }
            />
          </ListItem>
        </React.Fragment>
      );
    })}
  </List>
);
